/**
 * 권한 기반 라우트 가드 (HOC + Component)
 *
 * 지정된 권한을 가진 사용자만 자식 컴포넌트를 렌더링한다.
 * 권한이 없으면 404 NotFound를 표시하여 페이지 존재 여부 자체를 숨긴다.
 *
 * - AdminGuard는 USERID === 'admin' 전용 (관리자 페이지)
 * - PermissionGuard는 useAuth.hasPermission()을 사용 (일반 기능 페이지)
 *
 * 사용법 1 — HOC로 감싸기:
 *   export default withPermissionGuard('prd:pesticide:read')(PesticideList);
 *
 * 사용법 2 — 라우트 매핑 기반 (App.js에서 권장):
 *   <GuardedRoute path="/prd/pesticides" component={PesticideList} />
 *
 * 사용법 3 — JSX로 감싸기:
 *   <PermissionGuard permission="prd:pesticide:read"><Content /></PermissionGuard>
 *
 * React Hooks 규칙(조건부 return 시 이후 훅 호출 불가)을 지키기 위해
 * 외부 가드 컴포넌트에서 조건 검사 후 내부 Content 컴포넌트를 렌더한다.
 */
import React from 'react';
import { Route } from 'react-router-dom';
import { Spin } from 'antd';
import useAuth from '../../hooks/useAuth';
import NotFound from './NotFound';
import { getRoutePermission, ADMIN_ONLY } from '../../constants/routePermissions';

/**
 * 사용자의 권한이 라우트에 필요한 권한을 만족하는지 확인한다.
 * @param {Object} user - useAuth().user
 * @param {Function} hasPermission - useAuth().hasPermission
 * @param {string|null} required - 'prd:*:read' | 'ADMIN_ONLY' | null
 * @returns {boolean} 접근 허용 여부
 */
export function canAccessRoute(user, hasPermission, required) {
  if (required === null || required === undefined) return true; // 공개
  if (required === ADMIN_ONLY) {
    const trimmedId = String(user?.USERID || '').trim();
    return trimmedId === 'admin';
  }
  return hasPermission(required);
}

/**
 * 특정 권한 문자열을 요구하는 HOC
 * @param {string|null} permission - 'module:entity:action' 또는 null(공개)
 * @returns {Function} Component → Guarded Component 변환 함수
 */
export function withPermissionGuard(permission) {
  return function wrap(Component) {
    const GuardedComponent = (props) => {
      const { user, hasPermission, authInitialized } = useAuth();
      if (permission !== null && permission !== undefined && !authInitialized) return <Spin />;
      if (!canAccessRoute(user, hasPermission, permission)) return <NotFound />;
      return <Component {...props} />;
    };
    GuardedComponent.displayName =
      `withPermissionGuard(${Component.displayName || Component.name || 'Component'})`;
    return GuardedComponent;
  };
}

/**
 * JSX 래퍼 형태의 가드 컴포넌트
 * @param {Object} props
 * @param {string|null} props.permission
 * @param {React.ReactNode} props.children
 */
export function PermissionGuard({ permission, children }) {
  const { user, hasPermission, authInitialized } = useAuth();
  if (permission !== null && permission !== undefined && !authInitialized) return <Spin />;
  if (!canAccessRoute(user, hasPermission, permission)) return <NotFound />;
  return <>{children}</>;
}

/**
 * React Router Route 래퍼 — routePermissions.js의 매핑을 자동 적용한다.
 *
 * path가 여러 경로와 매칭되는 경우 (Switch 안에서) location.pathname을
 * 기준으로 권한을 조회하므로, 단일 Route 당 하나의 권한이 적용된다.
 *
 * 사용법:
 *   <GuardedRoute path="/prd/pesticides" component={PesticideList} />
 *   → 자동으로 ROUTE_PERMISSIONS['/prd/pesticides'] 권한 체크
 *
 * @param {Object} props - React Router Route props (path, exact, component 등)
 */
export function GuardedRoute({ component: Component, ...rest }) {
  return (
    <Route
      {...rest}
      render={(routeProps) => {
        const required = getRoutePermission(routeProps.match.path);
        return (
          <PermissionGuard permission={required}>
            <Component {...routeProps} />
          </PermissionGuard>
        );
      }}
    />
  );
}

export default PermissionGuard;
